import { useState, useEffect } from "react";
import { FaGithub, FaLinkedin, FaTwitter, FaEnvelope, FaCode, FaHtml5, FaCss3Alt, FaJs, FaReact, FaNodeJs, FaPhp, FaBuilding, FaUser, FaTools, FaArrowRight, FaPhone, FaWhatsapp, FaBriefcase, FaChartLine, FaAward, FaMapMarkerAlt, FaHome, FaComments, FaCheck, FaSun, FaMoon, FaGlobe } from 'react-icons/fa';
import { SiTailwindcss, SiTypescript, SiMongodb, SiPostgresql, SiMysql, SiExpress, SiAngular } from 'react-icons/si';
import { motion } from 'framer-motion';

// مكون القائمة الجانبية الثابتة - متطابق مع الصورة
function FixedSidebar() {
  const [activeSection, setActiveSection] = useState('home');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [language, setLanguage] = useState('en'); // 'en' or 'ar'
  
  // تطبيق الوضع الليلي
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // تطبيق اتجاه اللغة
  useEffect(() => {
    if (language === 'ar') {
      document.documentElement.setAttribute('dir', 'rtl');
    } else {
      document.documentElement.setAttribute('dir', 'ltr');
    }
  }, [language]);
  
  const menuItems = [
    { id: 'home', label: language === 'ar' ? 'الرئيسية' : 'Home', icon: FaHome, href: '#home' },
    { id: 'about', label: language === 'ar' ? 'حول' : 'About', icon: FaUser, href: '#about' },
    { id: 'achievements', label: language === 'ar' ? 'الإنجازات' : 'Achievements', icon: FaAward, href: '#achievements' },
    { id: 'projects', label: language === 'ar' ? 'المشاريع' : 'Projects', icon: FaCode, href: '#projects' },
    { id: 'dashboard', label: language === 'ar' ? 'لوحة التحكم' : 'Dashboard', icon: FaChartLine, href: '#dashboard' },
    { id: 'chat', label: language === 'ar' ? 'غرفة الدردشة' : 'Chat Room', icon: FaComments, href: '#chat' },
    { id: 'contact', label: language === 'ar' ? 'اتصل بنا' : 'Contact', icon: FaPhone, href: '#contact' }
  ];

  const handleSectionClick = (sectionId: string) => {
    setActiveSection(sectionId);
    
    // تمرير سلس للقسم المطلوب
    const element = document.getElementById(sectionId);
    if (element) {
      const headerHeight = 80;
      const elementPosition = element.offsetTop - headerHeight;
      
      window.scrollTo({
        top: elementPosition,
        behavior: 'smooth'
      });
    }
  };

  return (
    <div className="fixed left-0 top-0 h-full w-80 bg-white/95 dark:bg-gray-900/95 backdrop-blur-xl border-r border-gray-200/50 dark:border-gray-700/50 shadow-2xl z-50 overflow-y-auto flex flex-col">
      {/* قسم الملف الشخصي - متطابق مع الصورة */}
      <div className="p-6 border-b border-gray-200/50 dark:border-gray-700/50 bg-gradient-to-br from-blue-50/50 to-purple-50/50 dark:from-gray-800/50 dark:to-gray-900/50">
        <div className="text-center">
          {/* صورة الملف الشخصي */}
          <div className="w-24 h-24 mx-auto mb-4 rounded-full overflow-hidden border-2 border-white shadow-lg relative">
            <div className="absolute inset-0 rounded-full border-4 border-blue-500"></div>
            <img 
              src="/images/profile.jpeg" 
              alt="Satria Bahari"
              className="w-full h-full object-cover hover:scale-110 transition-transform duration-300 relative z-10"
              onError={(e) => {
                e.currentTarget.style.display = 'none';
                e.currentTarget.nextElementSibling?.classList.remove('hidden');
              }}
            />
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-purple-600 flex items-center justify-center hidden absolute inset-0 z-10">
              <FaUser className="text-white text-2xl" />
            </div>
          </div>
          
          {/* اسم المستخدم مع علامة التحقق */}
          <div className="flex items-center justify-center gap-2 mb-1">
            <h2 className="text-xl font-bold text-gray-900 dark:text-white">Satria Bahari</h2>
            <FaCheck className="text-blue-500 text-lg drop-shadow-sm" />
          </div>
          
          {/* اسم المستخدم */}
          <p className="text-gray-600 dark:text-gray-400 text-sm mb-4 font-medium">@satriabahari</p>
          
          {/* أزرار التحكم الصغيرة - متطابقة مع الصورة */}
          <div className="space-y-2 mb-4">
            {/* الصف الأول: أزرار التحكم */}
            <div className="flex items-center justify-center gap-2">
              <button className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center text-white text-sm hover:bg-green-600 transition-all duration-200 hover:scale-110 shadow-md">
                =
              </button>
              <button className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center text-white text-sm hover:bg-red-600 transition-all duration-200 hover:scale-110 shadow-md">
                -
              </button>
            </div>
            
            {/* الصف الثاني: أزرار الوضع الليلي واللغة */}
            <div className="flex items-center justify-center gap-2">
              <button 
                onClick={() => setIsDarkMode(!isDarkMode)}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-md ${
                  isDarkMode 
                    ? 'bg-gray-300 text-gray-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                {isDarkMode ? <FaMoon className="text-sm" /> : <FaSun className="text-sm" />}
              </button>
              <button 
                onClick={() => setLanguage(language === 'en' ? 'ar' : 'en')}
                className={`w-8 h-8 rounded-lg flex items-center justify-center transition-all duration-200 hover:scale-110 shadow-md ${
                  language === 'ar' 
                    ? 'bg-gray-300 text-gray-700' 
                    : 'bg-gray-200 text-gray-600'
                }`}
              >
                <FaGlobe className="text-sm" />
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* قائمة التنقل - متطابقة مع الصورة */}
      <nav className="p-4 flex-1">
        <div className="space-y-1">
          {menuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => handleSectionClick(item.id)}
              className={`w-full flex items-center justify-between p-3 rounded-lg transition-all duration-200 group ${
                activeSection === item.id
                  ? 'bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white shadow-sm'
                  : 'text-gray-700 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800/50'
              }`}
            >
              <div className="flex items-center gap-3">
                <item.icon className={`text-lg ${
                  activeSection === item.id 
                    ? 'text-gray-700 dark:text-gray-300' 
                    : 'text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-300'
                }`} />
                <span className="font-medium">{item.label}</span>
              </div>
              <FaArrowRight className={`text-sm transition-transform ${
                activeSection === item.id 
                  ? 'text-gray-700 dark:text-gray-300' 
                  : 'text-gray-400 group-hover:text-gray-600'
              }`} />
            </button>
          ))}
        </div>
      </nav>

      {/* زر Smart Talk - متطابق مع الصورة */}
      <div className="p-4 flex-1 flex items-end">
        <button className="w-full bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-4 rounded-lg transition-all duration-300 flex items-center justify-center gap-2 shadow-lg hover:shadow-xl hover:scale-105 active:scale-95">
          <FaComments className="text-lg" />
          {language === 'ar' ? 'المحادثة الذكية' : 'Smart Talk'}
        </button>
      </div>

      {/* حقوق النشر - متطابق مع الصورة */}
      <div className="p-4 border-t border-gray-200/50 dark:border-gray-700/50 bg-gray-50/50 dark:bg-gray-800/50">
        <p className="text-xs text-gray-500 dark:text-gray-400 text-center font-medium">
          {language === 'ar' 
            ? 'جميع الحقوق محفوظة © 2025 ساتريا باهاري' 
            : 'COPYRIGHT © 2025 Satria Bahari. All rights reserved.'
          }
        </p>
      </div>
    </div>
  );
}

// مكون المحتوى الرئيسي
function MainContent({ language }: { language: string }) {
  const [formData, setFormData] = useState({ name: '', email: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e: React.FormEvent) => { 
    e.preventDefault(); 
    setIsSubmitting(true);
    setSubmitMessage('');
    
    try {
      await new Promise(resolve => setTimeout(resolve, 1000));
      setSubmitMessage(language === 'ar' ? 'تم إرسال الرسالة بنجاح!' : 'Message sent successfully!');
      setFormData({ name: '', email: '', message: '' });
    } catch (error) {
      setSubmitMessage(language === 'ar' ? 'فشل في إرسال الرسالة. يرجى المحاولة مرة أخرى.' : 'Failed to send message. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  // بيانات الخبرات العملية
  const workExperience = [
    { 
      position: 'Senior Sales Associate', 
      company: 'Jarir Bookstore', 
      logo: '/images/Jarir.png',
      type: 'Full-time', 
      location: 'Jeddah, Saudi Arabia 🇸🇦',
      location_type: 'Onsite',
      start_date: '2022-09',
      end_date: '2024-10',
      link: 'https://jarir.com',
      responsibilities: [
        'Used CRM and Excel to analyze weekly sales reports, reducing stockouts by 20%.',
        'Generated visual performance reports to improve product strategy.',
        'Led a team of 10+ staff, monitoring KPIs and floor performance.',
        'Surpassed sales targets by 15% through data-driven promotions.'
      ]
    },
    { 
      position: 'Sales Associate', 
      company: 'Jarir Bookstore', 
      logo: '/images/Jarir.png',
      type: 'Full-time', 
      location: 'Jeddah, Saudi Arabia 🇸🇦',
      location_type: 'Onsite',
      start_date: '2017-08',
      end_date: '2022-08',
      link: 'https://jarir.com',
      responsibilities: [
        'Tracked customer behavior using CRM, increasing repeat sales by 10%.',
        'Delivered daily performance dashboards to management.',
        'Analyzed return patterns and suggested process refinements.',
        'Managed inventory levels to optimize stock availability.'
      ]
    },
    { 
      position: 'Frontend Developer', 
      company: 'Four Stars', 
      logo: '/images/4 Star.png',
      type: 'Internship', 
      location: 'Jeddah, Saudi Arabia 🇸🇦',
      location_type: 'Onsite',
      start_date: '2016-06',
      end_date: '2017-07',
      link: '#',
      responsibilities: [
        'Developed SQL-based reporting tools for sales and marketing teams.', 
        'Built internal dashboards to visualize KPIs and performance metrics.',
        'Automated workflows using scripts and macros.'
      ]
    },
    { 
      position: 'Full Stack Developer', 
      company: 'K6 Group', 
      logo: null,
      type: 'Freelance', 
      location: 'Remote 🌍',
      location_type: 'Remote',
      start_date: '2012-01',
      end_date: null,
      link: '#',
      responsibilities: [
        'Developed a student management system allowing admin to create courses and students to enroll, enhancing data management efficiency.',
        'Successfully cloned a project from Dolf Technologies Co Ltd showcasing strong technical skills and attention to detail.',
        'Created responsive web applications using modern JavaScript frameworks.',
        'Implemented data analysis solutions for various business requirements.'
      ]
    }
  ];

  return (
    <div className="ml-80 min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden relative">
      {/* خلفية متحركة محسنة */}
      <div className="absolute inset-0">
        <div className="absolute top-0 left-1/4 w-96 h-96 bg-blue-400/20 rounded-full blur-3xl animate-pulse-slow" />
        <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-purple-400/20 rounded-full blur-3xl animate-pulse-slow" style={{animationDelay: '2s'}} />
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-64 h-64 bg-indigo-400/10 rounded-full blur-2xl animate-pulse-slow" style={{animationDelay: '4s'}} />
        <div className="absolute top-1/4 right-1/4 w-48 h-48 bg-green-400/15 rounded-full blur-2xl animate-pulse-slow" style={{animationDelay: '1s'}} />
      </div>

      {/* المحتوى الرئيسي */}
      <main className="relative z-10 pt-8">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          
          {/* Hero Section - متطابق مع الصورة */}
          <section id="home" className="py-12 lg:py-16">
            <div className="text-center mb-16">
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8 }}
                className="mb-8"
              >
                <h1 className="text-4xl lg:text-6xl font-bold bg-gradient-to-r from-blue-600 via-purple-600 to-indigo-600 bg-clip-text text-transparent mb-4 drop-shadow-sm">
                  {language === 'ar' ? 'مرحباً، أنا ساتريا باهاري' : 'Hi, I&apos;m Satria Bahari'}
                </h1>
                <div className="flex justify-center gap-6 mb-6">
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-gray-800/50 px-3 py-1 rounded-full">
                    <span>{language === 'ar' ? 'مقيم في جامبي، إندونيسيا' : 'Based in Jambi, Indonesia'}</span>
                    <span>🇮🇩</span>
                  </div>
                  <div className="flex items-center gap-2 text-gray-600 dark:text-gray-300 bg-white/50 dark:bg-gray-800/50 px-3 py-1 rounded-full">
                    <span>{language === 'ar' ? 'في الموقع' : 'Onsite'}</span>
                  </div>
                </div>
                <p className="text-lg lg:text-xl text-gray-700 dark:text-gray-300 leading-relaxed mb-6 max-w-4xl mx-auto bg-white/30 dark:bg-gray-800/30 p-6 rounded-2xl backdrop-blur-sm border border-white/20 dark:border-gray-700/20">
                  {language === 'ar' 
                    ? 'مطور Fullstack ذو خبرة وشغف متخصص في بناء حلول رقمية قابلة للتطوير وفعالة. في الواجهة الأمامية، أعمل مع Next.js و TypeScript و Tailwind CSS لإنشاء واجهات مستخدم متجاوبة ومتاحة. في الخلفية، أطور خدمات قوية باستخدام Golang، وللجوال، أبني تطبيقات Android الأصلية باستخدام Kotlin. أنا مهندس تعاوني ومنظم التفاصيل ملتزم بتقديم تطبيقات منظمة وعالية الأداء عبر المنصات.'
                    : 'Experienced and passionate Fullstack Developer specializing in building scalable and efficient digital solutions. On the frontend, I work with Next.js, TypeScript, and Tailwind CSS to craft responsive and accessible user interfaces. On the backend, I develop robust services using Golang, and for mobile, I build native Android apps with Kotlin. I&apos;m a collaborative and detail-oriented engineer committed to delivering well-structured, high-performance applications across platforms.'
                  }
                </p>
              </motion.div>
            </div>
          </section>

          {/* قسم المهارات - متطابق مع الصورة */}
          <section id="about" className="py-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4 flex items-center justify-center gap-2">
                <FaCode className="text-blue-600 dark:text-blue-400" />
                &lt;/&gt; {language === 'ar' ? 'المهارات' : 'Skills'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                {language === 'ar' ? 'مهاراتي المهنية.' : 'My professional skills.'}
              </p>
            </motion.div>

            {/* شبكة المهارات - متطابقة مع الصورة */}
            <div className="grid grid-cols-4 md:grid-cols-6 lg:grid-cols-8 gap-4 mb-8 max-w-6xl mx-auto">
              {[
                { name: 'HTML5', icon: FaHtml5, color: 'text-orange-500' },
                { name: 'CSS3', icon: FaCss3Alt, color: 'text-blue-500' },
                { name: 'Bootstrap', icon: FaCode, color: 'text-purple-500' },
                { name: 'Tailwind CSS', icon: SiTailwindcss, color: 'text-cyan-500' },
                { name: 'JavaScript', icon: FaJs, color: 'text-yellow-500' },
                { name: 'TypeScript', icon: SiTypescript, color: 'text-blue-600' },
                { name: 'Vue.js', icon: FaCode, color: 'text-green-500' },
                { name: 'React', icon: FaReact, color: 'text-cyan-400' },
                { name: 'Redux', icon: FaCode, color: 'text-pink-500' },
                { name: 'Next.js', icon: FaCode, color: 'text-red-500' },
                { name: 'Node.js', icon: FaNodeJs, color: 'text-green-600' },
                { name: 'Vite', icon: FaCode, color: 'text-purple-600' },
                { name: 'Material-UI', icon: FaCode, color: 'text-blue-500' },
                { name: 'Sass', icon: FaCode, color: 'text-pink-400' },
                { name: 'Angular', icon: SiAngular, color: 'text-red-600' },
                { name: 'NestJS', icon: FaCode, color: 'text-red-700' },
                { name: 'Express.js', icon: SiExpress, color: 'text-gray-600' },
                { name: 'Golang', icon: FaCode, color: 'text-blue-500' },
                { name: 'PHP', icon: FaPhp, color: 'text-purple-600' },
                { name: 'Laravel', icon: FaCode, color: 'text-red-500' },
                { name: 'Dart', icon: FaCode, color: 'text-blue-500' },
                { name: 'Kotlin', icon: FaCode, color: 'text-purple-600' },
                { name: 'PostgreSQL', icon: SiPostgresql, color: 'text-blue-600' },
                { name: 'MySQL', icon: SiMysql, color: 'text-blue-500' },
                { name: 'MongoDB', icon: SiMongodb, color: 'text-green-600' },
                { name: 'Firebase', icon: FaCode, color: 'text-orange-500' },
                { name: 'Google Cloud', icon: FaCode, color: 'text-blue-500' },
                { name: 'Docker', icon: FaCode, color: 'text-blue-600' },
                { name: 'Kubernetes', icon: FaCode, color: 'text-blue-500' },
                { name: 'NPM', icon: FaCode, color: 'text-red-500' },
                { name: 'Git', icon: FaCode, color: 'text-orange-600' },
                { name: 'Figma', icon: FaCode, color: 'text-purple-500' },
                { name: 'GitHub', icon: FaGithub, color: 'text-gray-800 dark:text-gray-200' }
              ].map((skill, index) => (
                <motion.div
                  key={skill.name}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.05 }}
                  viewport={{ once: true }}
                  className="flex flex-col items-center gap-2 p-3 rounded-xl bg-white/90 dark:bg-gray-800/90 backdrop-blur-sm border border-white/30 dark:border-gray-700/30 hover:shadow-xl transition-all duration-300 group cursor-pointer"
                  whileHover={{ y: -8, scale: 1.1 }}
                >
                  <div className={`text-4xl ${skill.color} group-hover:scale-125 transition-transform duration-300`}>
                    <skill.icon />
                  </div>
                  <span className="text-xs text-gray-700 dark:text-gray-300 text-center font-semibold">
                    {skill.name}
                  </span>
                </motion.div>
              ))}
            </div>
          </section>

          {/* إحصائيات تفاعلية محسنة */}
          <section id="dashboard" className="py-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="grid grid-cols-2 lg:grid-cols-4 gap-6"
            >
              {[
                { number: '8+', label: language === 'ar' ? 'سنوات الخبرة' : 'Years Experience', icon: FaBriefcase, color: 'blue' },
                { number: '15+', label: language === 'ar' ? 'مشروع مكتمل' : 'Projects Completed', icon: FaCode, color: 'purple' },
                { number: '5+', label: language === 'ar' ? 'تقنيات' : 'Technologies', icon: FaTools, color: 'indigo' },
                { number: '100%', label: language === 'ar' ? 'رضا العملاء' : 'Client Satisfaction', icon: FaAward, color: 'green' }
              ].map((stat, index) => (
                <motion.div
                  key={stat.label}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 text-center border border-white/20 dark:border-gray-700/20 hover:shadow-xl transition-all duration-300"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className={`w-12 h-12 mx-auto mb-4 rounded-full bg-${stat.color}-100 dark:bg-${stat.color}-900/20 flex items-center justify-center`}>
                    <stat.icon className={`text-2xl text-${stat.color}-600 dark:text-${stat.color}-400`} />
                  </div>
                  <div className="text-3xl font-bold text-gray-900 dark:text-white mb-2">{stat.number}</div>
                  <div className="text-sm text-gray-600 dark:text-gray-400">{stat.label}</div>
                </motion.div>
              ))}
            </motion.div>
          </section>

          {/* الجدول الزمني للخبرات محسن */}
          <section id="achievements" className="py-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'الخبرة المهنية' : 'Professional Experience'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                {language === 'ar' ? 'رحلتي في التكنولوجيا وتحليل البيانات' : 'My journey in technology and data analysis'}
              </p>
            </motion.div>

            <div className="relative">
              {/* خط زمني */}
              <div className="absolute left-4 lg:left-1/2 top-0 bottom-0 w-0.5 bg-gradient-to-b from-blue-500 to-purple-500 transform lg:translate-x-px" />
              
              <div className="space-y-8">
                {workExperience.map((job, index) => (
                  <motion.div
                    key={job.position}
                    initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.8, delay: index * 0.2 }}
                    viewport={{ once: true }}
                    className={`relative flex items-center ${index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'}`}
                  >
                    {/* نقطة على الخط الزمني */}
                    <div className="absolute left-4 lg:left-1/2 w-4 h-4 bg-blue-500 rounded-full border-4 border-white dark:border-gray-800 transform lg:translate-x-2 z-10" />
                    
                    {/* محتوى الوظيفة */}
                    <div className={`ml-12 lg:ml-0 lg:w-5/12 ${index % 2 === 0 ? 'lg:mr-auto lg:pr-8' : 'lg:ml-auto lg:pl-8'}`}>
                      <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 dark:border-gray-700/20 hover:shadow-xl transition-all duration-300">
                        <div className="flex items-start gap-4 mb-4">
                          {job.logo ? (
                            <img
                              src={job.logo}
                              alt={job.company}
                              className="w-12 h-12 rounded-lg object-cover"
                            />
                          ) : (
                            <div className="w-12 h-12 bg-gray-200 dark:bg-gray-700 rounded-lg flex items-center justify-center">
                              <FaBuilding className="text-gray-500 dark:text-gray-400" />
                            </div>
                          )}
                          <div className="flex-1">
                            <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{job.position}</h3>
                            <p className="text-blue-600 dark:text-blue-400 font-medium">{job.company}</p>
                            <p className="text-sm text-gray-600 dark:text-gray-400">{job.location}</p>
                          </div>
                        </div>
                        <div className="flex flex-wrap gap-2 mb-4">
                          <span className="px-3 py-1 bg-blue-100 dark:bg-blue-900/20 text-blue-700 dark:text-blue-300 rounded-full text-xs">
                            {job.type}
                          </span>
                          <span className="px-3 py-1 bg-purple-100 dark:bg-purple-900/20 text-purple-700 dark:text-purple-300 rounded-full text-xs">
                            {job.location_type}
                          </span>
                        </div>
                        <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                          {job.start_date} - {job.end_date || 'Present'}
                        </p>
                        <ul className="space-y-2">
                          {job.responsibilities.slice(0, 3).map((responsibility, idx) => (
                            <li key={idx} className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                              <span className="w-1.5 h-1.5 mt-2 rounded-full bg-blue-500 flex-shrink-0" />
                              {responsibility}
                            </li>
                          ))}
                        </ul>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </div>
          </section>

          {/* المشاريع محسنة */}
          <section id="projects" className="py-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'المشاريع المميزة' : 'Featured Projects'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                {language === 'ar' ? 'عرض لأعمالي الحديثة وقدراتي التقنية' : 'A showcase of my recent work and technical capabilities'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[
                {
                  title: 'Student Management System',
                  description: 'Full-stack application for course management and student enrollment',
                  tech: ['React', 'Node.js', 'MongoDB'],
                  color: 'blue'
                },
                {
                  title: 'Sales Analytics Dashboard',
                  description: 'Data visualization platform for sales performance tracking',
                  tech: ['Python', 'Chart.js', 'Express'],
                  color: 'green'
                },
                {
                  title: 'E-commerce Platform',
                  description: 'Modern online store with payment integration',
                  tech: ['Next.js', 'Stripe', 'PostgreSQL'],
                  color: 'purple'
                },
                {
                  title: 'Portfolio Website',
                  description: 'Responsive personal portfolio with modern design',
                  tech: ['React', 'Tailwind CSS', 'Framer Motion'],
                  color: 'indigo'
                },
                {
                  title: 'Data Analysis Tool',
                  description: 'Python-based tool for statistical analysis',
                  tech: ['Python', 'Pandas', 'Matplotlib'],
                  color: 'orange'
                },
                {
                  title: 'Task Management App',
                  description: 'Collaborative task management with real-time updates',
                  tech: ['React', 'Socket.io', 'MongoDB'],
                  color: 'cyan'
                }
              ].map((project, index) => (
                <motion.div
                  key={project.title}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-6 border border-white/20 dark:border-gray-700/20 hover:shadow-xl transition-all duration-300 group"
                  whileHover={{ y: -5, scale: 1.02 }}
                >
                  <div className={`w-12 h-12 mb-4 rounded-lg bg-${project.color}-100 dark:bg-${project.color}-900/20 flex items-center justify-center group-hover:scale-110 transition-transform`}>
                    <FaCode className={`text-2xl text-${project.color}-600 dark:text-${project.color}-400`} />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">{project.title}</h3>
                  <p className="text-gray-600 dark:text-gray-400 text-sm mb-4">{project.description}</p>
                  <div className="flex flex-wrap gap-2">
                    {project.tech.map((tech) => (
                      <span key={tech} className="px-2 py-1 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </motion.div>
              ))}
            </div>
          </section>

          {/* التواصل محسن */}
          <section id="contact" className="py-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'تواصل معي' : 'Get In Touch'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                {language === 'ar' ? 'جاهز للتعاون؟ دعنا نناقش مشروعك القادم' : 'Ready to collaborate? Let&apos;s discuss your next project'}
              </p>
            </motion.div>

            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* معلومات التواصل */}
              <motion.div
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 dark:border-gray-700/20"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                  {language === 'ar' ? 'معلومات التواصل' : 'Contact Information'}
                </h3>
                <div className="space-y-4">
                  {[
                    { icon: FaEnvelope, label: language === 'ar' ? 'البريد الإلكتروني' : 'Email', value: 'osama@oz.sa', href: 'mailto:osama@oz.sa' },
                    { icon: FaPhone, label: language === 'ar' ? 'الهاتف' : 'Phone', value: '+966 55 449 1270', href: 'tel:+966554491270' },
                    { icon: FaWhatsapp, label: 'WhatsApp', value: '+966 55 449 1270', href: 'https://wa.me/966554491270' },
                    { icon: FaMapMarkerAlt, label: language === 'ar' ? 'الموقع' : 'Location', value: language === 'ar' ? 'جدة، المملكة العربية السعودية' : 'Jeddah, Saudi Arabia', href: null }
                  ].map((contact, index) => (
                    <motion.div
                      key={contact.label}
                      initial={{ opacity: 0, x: -20 }}
                      whileInView={{ opacity: 1, x: 0 }}
                      transition={{ duration: 0.5, delay: index * 0.1 }}
                      viewport={{ once: true }}
                      className="flex items-center gap-4"
                    >
                      <div className="w-10 h-10 bg-blue-100 dark:bg-blue-900/20 rounded-lg flex items-center justify-center">
                        <contact.icon className="text-blue-600 dark:text-blue-400" />
                      </div>
                      <div>
                        <p className="text-sm text-gray-600 dark:text-gray-400">{contact.label}</p>
                        {contact.href ? (
                          <a
                            href={contact.href}
                            className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
                          >
                            {contact.value}
                          </a>
                        ) : (
                          <p className="text-gray-900 dark:text-white">{contact.value}</p>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              </motion.div>

              {/* نموذج التواصل */}
              <motion.div
                initial={{ opacity: 0, x: 30 }}
                whileInView={{ opacity: 1, x: 0 }}
                transition={{ duration: 0.8 }}
                viewport={{ once: true }}
                className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 dark:border-gray-700/20"
              >
                <h3 className="text-xl font-semibold text-gray-900 dark:text-white mb-6">
                  {language === 'ar' ? 'إرسال رسالة' : 'Send Message'}
                </h3>
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {language === 'ar' ? 'الاسم' : 'Name'}
                    </label>
                    <input
                      type="text"
                      id="name"
                      name="name"
                      value={formData.name}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {language === 'ar' ? 'البريد الإلكتروني' : 'Email'}
                    </label>
                    <input
                      type="email"
                      id="email"
                      name="email"
                      value={formData.email}
                      onChange={handleInputChange}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <div>
                    <label htmlFor="message" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                      {language === 'ar' ? 'الرسالة' : 'Message'}
                    </label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleInputChange}
                      rows={4}
                      className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent dark:bg-gray-700 dark:text-white resize-none"
                      required
                      disabled={isSubmitting}
                    />
                  </div>
                  <motion.button
                    type="submit"
                    disabled={isSubmitting}
                    className="w-full bg-gradient-to-r from-blue-600 to-purple-600 text-white py-3 px-6 rounded-lg font-semibold hover:from-blue-700 hover:to-purple-700 transition-all duration-300 disabled:opacity-50 disabled:cursor-not-allowed"
                    whileHover={{ scale: 1.02 }}
                    whileTap={{ scale: 0.98 }}
                  >
                    {isSubmitting ? (language === 'ar' ? 'جاري الإرسال...' : 'Sending...') : (language === 'ar' ? 'إرسال الرسالة' : 'Send Message')}
                  </motion.button>
                  {submitMessage && (
                    <motion.p
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`text-sm text-center ${
                        submitMessage.includes('success') || submitMessage.includes('نجاح') ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'
                      }`}
                    >
                      {submitMessage}
                    </motion.p>
                  )}
                </form>
              </motion.div>
            </div>
          </section>

          {/* قسم الدردشة */}
          <section id="chat" className="py-12">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8 }}
              viewport={{ once: true }}
              className="text-center mb-12"
            >
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'غرفة الدردشة' : 'Chat Room'}
              </h2>
              <p className="text-lg text-gray-600 dark:text-gray-300 max-w-2xl mx-auto">
                {language === 'ar' ? 'تواصل معي في الوقت الفعلي' : 'Connect with me in real-time'}
              </p>
            </motion.div>

            <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm rounded-2xl p-8 border border-white/20 dark:border-gray-700/20 text-center">
              <div className="w-24 h-24 mx-auto mb-6 bg-green-100 dark:bg-green-900/20 rounded-full flex items-center justify-center">
                <FaComments className="text-4xl text-green-600 dark:text-green-400" />
              </div>
              <h3 className="text-2xl font-bold text-gray-900 dark:text-white mb-4">
                {language === 'ar' ? 'المحادثة الذكية' : 'Smart Talk'}
              </h3>
              <p className="text-gray-600 dark:text-gray-300 mb-6">
                {language === 'ar' 
                  ? 'جاهز لبدء محادثة؟ انقر على الزر أدناه للتواصل معي.'
                  : 'Ready to start a conversation? Click the button below to connect with me.'
                }
              </p>
              <motion.button
                className="bg-green-500 hover:bg-green-600 text-white font-bold py-3 px-8 rounded-lg transition-colors duration-200 flex items-center gap-2 mx-auto"
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                <FaComments className="text-lg" />
                {language === 'ar' ? 'ابدأ المحادثة' : 'Start Chat'}
              </motion.button>
            </div>
          </section>
        </div>
      </main>

      {/* Footer محسن */}
      <footer className="relative z-10 mt-12 bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm border-t border-white/20 dark:border-gray-700/20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-purple-600 rounded-lg flex items-center justify-center">
                <FaUser className="text-white text-sm" />
              </div>
              <span className="font-bold text-gray-900 dark:text-white">
                {language === 'ar' ? 'ساتريا باهاري' : 'Satria Bahari'}
              </span>
            </div>
            <div className="flex items-center gap-4">
              <a href="https://github.com/zk9c" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <FaGithub className="text-xl" />
              </a>
              <a href="https://linkedin.com/in/oszu" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <FaLinkedin className="text-xl" />
              </a>
              <a href="https://x.com/OZ7SA/" className="text-gray-600 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                <FaTwitter className="text-xl" />
              </a>
            </div>
            <div className="text-sm text-gray-600 dark:text-gray-400">
              © {new Date().getFullYear()} {language === 'ar' ? 'ساتريا باهاري' : 'Satria Bahari'}. {language === 'ar' ? 'جميع الحقوق محفوظة' : 'All rights reserved'}.
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default function AboutPage() {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-100 dark:from-gray-900 dark:via-gray-800 dark:to-gray-900 overflow-hidden relative">
      {/* القائمة الجانبية الثابتة */}
      <FixedSidebar />
      
      {/* المحتوى الرئيسي */}
      <MainContent language="en" />
    </div>
  );
} 